import _ from 'lodash'
import { Observable, of, timer } from 'rxjs'
import { catchError,  delayWhen, first, map, retryWhen, switchMap, tap} from 'rxjs/operators'

import { SearchResponse, AuditCampaign, Record, RecordLifecycleState, PushCachedValueResult, ProjectLinkableItemStateResource } from '@skinsoft/types'
import { auditCampaignSelectors } from '@skinsoft/selectors'
import { OfflineDataFetcher, AuditCampaignOfflineDataFetcher } from '@skinsoft/offline'
import { EntityDataStore } from '@skinsoft/entity-data-store'
import { ItemDataStore } from '@skinsoft/entity'
import { RecordDataStore } from '@skinsoft/entity'

export class AuditCampaignDataStore extends EntityDataStore<AuditCampaign>{

    public static getInstance() {
        if (!AuditCampaignDataStore.instance) {
            AuditCampaignDataStore.instance = new AuditCampaignDataStore();
        }

        return AuditCampaignDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$auditCampaignDataStore = AuditCampaignDataStore.getInstance();
    }

    private static instance: AuditCampaignDataStore = null as any;

    constructor() {
        super('audit-projects', ['auditProject']);
    }

    public exists(id: AuditCampaign['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    protected pushCachedUpdate(dataToUpdate): Observable<PushCachedValueResult> {
        if (dataToUpdate.id && dataToUpdate.body) {
            const newItems = dataToUpdate.body.workflowSchema.linkableItems.filter(item => OfflineDataFetcher.isTemporaryId(item.linkableItem))
            dataToUpdate.body.workflowSchema.linkableItems = dataToUpdate.body.workflowSchema.linkableItems.filter(item => !OfflineDataFetcher.isTemporaryId(item.linkableItem))
        
            const auditRecordsBeforePush = auditCampaignSelectors.auditRecords(dataToUpdate.body).sort()

            return this.update(
                dataToUpdate.id,
                dataToUpdate.body,
                undefined,
                true
            )
                .pipe(
                    tap(body => {
                        newItems.forEach(item => {
                            const itemDataStore = ItemDataStore.getInstance()

                            of('_').pipe(
                                map(() => itemDataStore.getUpdatedValue(item.linkableItem)),
                                tap(id=> {
                                    if (!id || id === 'inProgress') {
                                        throw new Error('not yet updated')
                                    }
                                }),
                                retryWhen(errors => errors.pipe(delayWhen(() => timer(1000))))
                            ).subscribe(async newItemId => {
                                this.addItem(body, newItemId!, 'PhysicalMuseumItem')

                                const localRecord = await RecordDataStore.getInstance()
                                    .searchAllOffline({
                                        page: 0,
                                        pageSize: 1,
                                        types: ['AuditRecord'],
                                        filteredIds: auditRecordsBeforePush,
                                        advancedSearches: [{
                                            leftCriteria: {
                                                entityField: '5d5d3797bbffbb20bbcca5fa',// linked item
                                                operator: 'equals',
                                                type: 'criteria',
                                                value: {
                                                    completeSearch: false,
                                                    refId: item.linkableItem,
                                                    type: 'ref'
                                                }
                                            },
                                            linkOperator: null,
                                            rightCriteria: null,
                                            type: 'requestSearch'
                                        }],
                                    })
                                    .toPromise()

                                const distantRecord = await of('_').pipe(
                                    switchMap(() => AuditCampaignDataStore.getInstance().fetchOne(body.id)),
                                    switchMap((newBody) => {
                                        return RecordDataStore.getInstance().searchAll({
                                            page: 0,
                                            pageSize: 1,
                                            types: ['Recolement'],
                                            filteredIds: auditCampaignSelectors.auditRecords(newBody),
                                            advancedSearches: [{
                                                leftCriteria: {
                                                    entityField: '5d5d3797bbffbb20bbcca5fa',// linked item
                                                    operator: 'equals',
                                                    type: 'criteria',
                                                    value: {
                                                        completeSearch: false,
                                                        refId: newItemId,
                                                        type: 'ref'
                                                    }
                                                },
                                                linkOperator: null,
                                                rightCriteria: null,
                                                type: 'requestSearch'
                                            }],
                                        })
                                    }),
                                    tap(result => {
                                        if ((result as SearchResponse<Record>).totalSize === 0) {
                                            throw new Error('not yet created')
                                        }
                                    }),
                                    retryWhen(errors => errors.pipe(delayWhen(() => timer(1000))))
                                ).toPromise()
                                const toUpload = this.fixLinkedItem(this.cleanRefs(this.recursiveGenericMerge(distantRecord.allData[0], localRecord.allData[0])))
                                toUpload.id = distantRecord.allData[0].id
                                _.set(toUpload, 'recolementIdentificationSchema.recoleur', _.get(distantRecord.allData[0], 'recolementIdentificationSchema.recoleur'));
                                _.set(toUpload, 'institutionSharing', _.get(distantRecord.allData[0], 'institutionSharing'));
                                RecordDataStore.getInstance().update(distantRecord.allData[0].id, toUpload, undefined, true).subscribe(data => RecordDataStore.getInstance().removeOfflineData(localRecord.allData[0].id))        
                            })
                        })
                    }),
                    first(),
                    // Handle success or error if the update and emit boolean
                    catchError(e => of(Error('Error during update...'))),
                    switchMap(
                        response => {
                            if (!(response instanceof Error)) {
                                this.offlineDataUpdateCacher.removeCachedValue(dataToUpdate.id);
                                return of({ success: true })
                            }
                            return of({ success: false });
                        }
                    )
                )
        }
        else {
            return of({ success: true });
        }
    }

    public cleanRefs(obj) {
        if (obj && typeof obj === 'object') {
            if (obj.ref) {
                return obj.ref
            }
            let keys = Object.keys(obj)
            const clean = {}
            for (const field of keys) {
                if (Array.isArray(obj[field])) {
                    clean[field] = obj[field].map(e => this.cleanRefs(e))
                } else if (typeof obj[field] === 'object') {
                    clean[field] = this.cleanRefs(obj[field])
                } else {
                    clean[field] = obj[field]
                }
            }

            return clean
        }

        return obj
    }

    public fixLinkedItem(obj) {
        obj.recolementLinkSchema.linkedItem = { linkableItem: obj.recolementLinkSchema.linkedItem }
        return obj
    }

    public update(id: string, body: AuditCampaign, endpointOverride?: string, ignoreCachedStatus: boolean = false) {
        if (window.navigator.onLine) {
            // Saving records with local ids
            const auditRecordsBeforeUpdate = auditCampaignSelectors.auditRecords(body).sort();

            // Removing temporary record ids to avoid Error 500 on update
            body.auditProjectSchema.auditReports = auditCampaignSelectors.auditRecords(body).filter(recordId => !OfflineDataFetcher.isTemporaryId(recordId))

            const linkableItemsBeforeUpdate = auditCampaignSelectors.linkableItems(body)

            body.workflowSchema.linkableItems = auditCampaignSelectors.linkableItems(body).filter(linkableItem => {
                return linkableItem.linkableItem?.linkableItem && 
                    !OfflineDataFetcher.isTemporaryId(linkableItem.linkableItem?.linkableItem)
            })

            let needOfflineUpdate = true

            // Updating campaign to create missing records from linkable items
            return super.update(id, body, endpointOverride, ignoreCachedStatus).pipe(
                switchMap(response => this.fetchOne(id)),
                tap(
                    response => {
                        // Retieving records, including the new ones freshly created
                        const auditRecordsAfterUpdate = auditCampaignSelectors.auditRecords(response).sort();

                        // Getting the created records that are not in cache yet (? perfs with a lot of records ?)
                        const diff = auditRecordsAfterUpdate.filter(
                            recordId => !auditRecordsBeforeUpdate.includes(recordId)
                        )

                        // Replacing temp ids in local records and pushing them to db
                        Promise.all(auditRecordsBeforeUpdate
                            .filter(recordId => OfflineDataFetcher.isTemporaryId(recordId)) // keep only ids of locally created entities
                            .map(
                                async (recordId, index) => {
                                    const dbId = diff[index]
                                    const updateBody = await RecordDataStore.getInstance().getObjectInUpdateCache(recordId);
                                    if (dbId) {
                                        const existingBody = await RecordDataStore.getInstance().fetchOne(dbId).pipe(first()).toPromise();

                                        needOfflineUpdate = false 
                                        
                                        // Replacing  temporary id in body
                                        updateBody.id = dbId;

                                        // Keeping readonly fields to avoid error on update
                                        _.set(updateBody, 'recolementIdentificationSchema.recoleur', _.get(existingBody, 'recolementIdentificationSchema.recoleur'));
                                        _.set(updateBody, 'recolementIdentificationSchema.authorizedAuditor', _.get(existingBody, 'recolementIdentificationSchema.authorizedAuditor'));
                                        _.set(updateBody, 'institutionSharing', _.get(existingBody, 'institutionSharing'));

                                        RecordDataStore.getInstance().update(dbId, updateBody, undefined, ignoreCachedStatus).subscribe(
                                            saveResponse => {
                                                // Removing freshly saved record from cache
                                                RecordDataStore.getInstance().removeObjectFromUpdateCache(recordId);
                                            }
                                        );
                                    }
                                }
                            ))
                        if (needOfflineUpdate) {
                            body.workflowSchema.linkableItems = linkableItemsBeforeUpdate
                            body.auditProjectSchema.auditReports = auditRecordsBeforeUpdate
                            super.updateOfflineMode(id, body)
                        }
                    })
            );
        }
        return super.update(id, body, endpointOverride, ignoreCachedStatus);
    }

    public async addItem(
        auditCampaign: AuditCampaign,
        newItemId: string,
        itemType: string
    ): Promise<any> {
        const linkableItems: ProjectLinkableItemStateResource[] = auditCampaignSelectors.linkableItems(auditCampaign);
        const newLinkedItem: ProjectLinkableItemStateResource = {
            linkableItem: {
                linkableItem: newItemId,
                linkableItemReferenceType: itemType   
            },
        }
        linkableItems.push(newLinkedItem)

        auditCampaign.workflowSchema.linkableItems = linkableItems;

        // Creating local record if offline mode or item is not online yet
        if (!window.navigator.onLine || OfflineDataFetcher.isTemporaryId(newItemId)) {
            const newRecord = await this.cloneRecord(auditCampaign.id, newLinkedItem);
            if (newRecord) {
                auditCampaign.auditProjectSchema.auditReports.push(newRecord.id)
            }
        }

        return await this.update(auditCampaign.id, auditCampaign)
            .pipe(first())
            .toPromise();
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new AuditCampaignOfflineDataFetcher(this.sanitizedIdentifer);
    }

    /**
     * Creates a blank record from an existing one
     * @param campaignId 
     * @param itemId 
     */
    private async cloneRecord(campaignId: string, linkedItem: any) {
        const campaign = await this.fetchOne(campaignId).pipe(first()).toPromise();
        const recordToClone = await RecordDataStore.getInstance().fetchRandomRecord(OfflineDataFetcher.isTemporaryId(linkedItem.linkableItem)).pipe(first()).toPromise();
        const blankRecord: Record = await this.resetRecord(recordToClone, campaign);
        _.set(blankRecord, 'recolementLinkSchema.linkedItem', linkedItem)

        if (OfflineDataFetcher.isTemporaryId(linkedItem.linkableItem)) {
            return RecordDataStore.getInstance().createOffline(blankRecord).pipe(first()).toPromise();
        }
        return RecordDataStore.getInstance().create({}, blankRecord).pipe(first()).toPromise();
    }

    private async resetRecord(record: Record, campaign: AuditCampaign) {
        // Removing identifiers to avoid doubles and conflicts (409)
        _.set(record, 'recolementIdentificationSchema.numberIdentifier', "");
        _.set(record, 'title', "");
        _.set(record, 'workflowSchema.currentState.name', RecordLifecycleState.IN_PROGRESS)
        _.set(record, 'managementData.creationDate', null)
        _.set(record, 'managementData.updatedDate', null)
        _.set(record, 'managementData.creatorUser', null)
        _.set(record, 'managementData.updaterUser', null)
        _.set(record, 'recolementIdentificationSchema.validationDate', null)
        return record;
    }
}